import React, { memo } from "react";

import RenderProvenance from "./renderProvenance.js";
import styles from "./summary.module.scss";

/**
 * Narration controls for one chapter: generate it, watch it, play it.
 *
 * The player source is a short-lived signed URL the API attaches as
 * `playbackUrl` — generated audio lives in a private bucket and has no stored
 * URL to fall back on.
 */
const STATUS_LABEL = {
  queued: "Queued…",
  running: "Rendering…",
  failed: "Failed",
  completed: "Done",
};

const ChapterNarration = ({
  audio,
  job,
  chapterId,
  onGenerate,
  disabled,
  providers = [],
  provider,
  onProviderChange,
}) => {
  // No chapter id means this chapter has never been saved, so the backend has
  // nothing to attach audio to. Prompting to generate would fail confusingly.
  if (!chapterId) {
    return (
      <p className={styles.narrationHint}>
        Save the summary before generating narration for this chapter.
      </p>
    );
  }

  const src = audio?.playbackUrl || null;
  const active = job?.status === "queued" || job?.status === "running";
  const hasAudio = Boolean(src);

  return (
    <div className={styles.narration}>
      <div className={styles.narrationHeader}>
        <label className="label mb-0">Narration</label>
        <div className="d-flex align-items-center gap-2">
          {/* Which narrator renders this. A real choice, not a setting: the
              hosted one bills per character and runs immediately, the GPU one
              costs hours and uses the reference voice loaded on the pod — and
              testing a reference voice is the whole reason to pick it while
              the hosted one is configured. Hidden when there is only one. */}
          {providers.length > 1 && (
            <select
              className={styles.narrationProvider}
              value={provider || ""}
              disabled={disabled || active}
              onChange={(e) => onProviderChange?.(e.target.value)}
              title={providers.find((p) => p.id === provider)?.detail || "Who renders this"}
            >
              {providers.map((p) => (
                <option key={p.id} value={p.id} disabled={!p.available}>
                  {p.label}{p.available ? "" : " (not configured)"}
                </option>
              ))}
            </select>
          )}
          {job && job.status !== "completed" && (
            <span className={job.status === "failed" ? styles.narrationFailed : styles.narrationBusy}>
              {STATUS_LABEL[job.status] ?? job.status}
            </span>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            // "Regenerate" means render it again. Without force the backend
            // skips a chapter whose text hasn't changed, which is exactly the
            // case when the voice or pause settings are what changed.
            onClick={() => onGenerate(chapterId, { force: hasAudio })}
            disabled={disabled || active}
            title="Narrates this chapter in the language shown above"
          >
            {active ? "Working…" : hasAudio ? "Regenerate" : "Generate audio"}
          </button>
          {/* The same shape the translate button has: one click, every
              language, rather than five tabs and remembering which are done.
              Hindi is absent because the model cannot speak it. */}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onGenerate(chapterId, { allLanguages: true })}
            disabled={disabled || active}
            title="Narrates this chapter in English, German, French, Spanish and Japanese. Languages this summary has not been translated into are skipped."
          >
            All languages
          </button>
        </div>
      </div>

      {job?.status === "failed" && job.error && (
        <p className={styles.narrationError}>{job.error}</p>
      )}

      {hasAudio ? (
        <>
          <audio controls className="mt-2 w-100" src={src}>
            <track kind="captions" />
          </audio>
          <RenderProvenance audio={audio} />
        </>
      ) : (
        !active && <p className={styles.narrationHint}>No narration yet.</p>
      )}
    </div>
  );
};

export default memo(ChapterNarration);
